import type * as React from "react"

import { cn } from "@/utils/styles.helpers"

export interface SectionProps
	extends Omit<React.ComponentProps<"section">, "title"> {
	/** The section label, shown as 11px uppercase meta text. */
	label: React.ReactNode
	/** An optional control on the right of the label, such as a link or a small button. */
	action?: React.ReactNode
}

/**
 * A labelled block of a page: the label (with an optional action on the
 * right), a 12px gap, then the content. Sections stack inside `SectionStack`.
 */
export function Section({
	label,
	action,
	className,
	children,
	...props
}: SectionProps) {
	return (
		<section
			data-slot="section"
			className={cn("flex min-w-0 flex-col gap-3", className)}
			{...props}
		>
			<div className="flex min-h-5 items-center justify-between gap-2">
				<h2 className="text-[11px] leading-4 font-semibold tracking-[0.07em] text-meta uppercase">
					{label}
				</h2>
				{action}
			</div>
			{children}
		</section>
	)
}

export type SectionStackProps = React.ComponentProps<"div">

/** Stacks sections with the 32px gap between them. */
export function SectionStack({ className, ...props }: SectionStackProps) {
	return (
		<div
			data-slot="section-stack"
			className={cn("flex min-w-0 flex-col gap-8", className)}
			{...props}
		/>
	)
}
